import React, { useState, useRef, useEffect, useMemo } from "react";
import "./PlanDropdown.css";

const PlanDropdown = ({
  sizes,
  value,
  onChange,
  requiredMinDisk = 0,
  onInvalidSelect,
  currency = "INR",
  region,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const selectedSize = sizes.find((s) => s.slug === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const isSizeDisabled = (size) => {
    if (!requiredMinDisk) return false;
    return (size?.disk || 0) < requiredMinDisk;
  };

  const disabledReason = (size) => {
    if (!isSizeDisabled(size)) return "";
    return `Requires ≥ ${requiredMinDisk}GB disk`;
  };

  const isRegionCompatible = (size) => {
    if (!region) return true;
    const regions = size?.regions;
    if (!Array.isArray(regions) || regions.length === 0) return true;
    return regions.includes(region);
  };

  const getDisabledMessage = (size) => {
    if (!isRegionCompatible(size)) return "Not available in selected region";
    if (isSizeDisabled(size)) return disabledReason(size);
    return "";
  };

  // Filter sizes based on search
  const filteredSizes = useMemo(() => {
    const searchLower = search.toLowerCase();
    return sizes.filter((size) => {
      return (
        size.slug.toLowerCase().includes(searchLower) ||
        size.description?.toLowerCase().includes(searchLower)
      );
    });
  }, [sizes, search]);

  const handleSelect = (size) => {
    const message = getDisabledMessage(size);
    if (message) {
      if (typeof onInvalidSelect === "function") onInvalidSelect(message);
      return;
    }
    onChange(size.slug);
    setIsOpen(false);
    setSearch("");
  };

  const formatPrice = (size) => {
    const ccy = (currency || "INR").toString().toUpperCase();
    if (ccy === "USD") {
      const usd =
        size?.pricing?.usdWithMargin ??
        size?.pricing?.usd ??
        (typeof size?.price_monthly === "string"
          ? parseFloat(size.price_monthly)
          : size?.price_monthly);
      const num = Number(usd);
      if (!Number.isNaN(num)) return `$${num.toFixed(2)}/mo`;
      return "$/mo";
    }

    // Default INR
    if (size.pricing && typeof size.pricing.inr === "number") {
      return `₹${size.pricing.inr}/mo`;
    }
    return `₹${size.price_monthly}/mo`;
  };

  const formatSpecs = (size) => {
    return `${size.vcpus} vCPU${size.vcpus > 1 ? "s" : ""} • ${
      size.memory / 1024
    }GB RAM • ${size.disk}GB ${getCpuTypeLabel(size)}`;
  };

  const getCpuTypeLabel = (size) => {
    if (
      size.description?.toLowerCase().includes("amd") ||
      size.description?.toLowerCase().includes("ryzen")
    ) {
      return "NVMe (AMD)";
    }
    if (
      size.description?.toLowerCase().includes("intel") ||
      size.description?.toLowerCase().includes("premium") ||
      size.slug?.includes("c-") ||
      size.slug?.includes("g-")
    ) {
      return "NVMe (Intel)";
    }
    return "SSD";
  };

  return (
    <div className="plan-dropdown" ref={dropdownRef}>
      <div
        className={`plan-dropdown-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="plan-dropdown-trigger-content">
          {selectedSize ? (
            <>
              <div className="plan-dropdown-text">
                <div className="plan-dropdown-label">{selectedSize.slug}</div>
                <div className="plan-dropdown-sublabel">
                  {formatSpecs(selectedSize)}
                </div>
              </div>
              <div className="plan-dropdown-price">
                {formatPrice(selectedSize)}
              </div>
            </>
          ) : (
            <div className="plan-dropdown-text">
              <div className="plan-dropdown-label">Select a plan</div>
            </div>
          )}
        </div>
        <svg
          className={`plan-dropdown-arrow ${isOpen ? "rotated" : ""}`}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="plan-dropdown-menu">
          <div className="plan-dropdown-search">
            <svg
              className="plan-search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L10.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search plans..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div className="plan-dropdown-options">
            {filteredSizes.length > 0 ? (
              filteredSizes.map((size) => (
                <div
                  key={size.slug}
                  className={`plan-dropdown-option ${
                    value === size.slug ? "selected" : ""
                  } ${getDisabledMessage(size) ? "disabled" : ""}`}
                  onClick={() => handleSelect(size)}
                  role="button"
                  aria-disabled={!!getDisabledMessage(size)}
                >
                  <div className="plan-dropdown-option-content">
                    <div className="plan-dropdown-option-header">
                      <div className="plan-dropdown-option-name">
                        {size.slug}
                      </div>
                      <div className="plan-dropdown-option-price">
                        {formatPrice(size)}
                      </div>
                    </div>
                    <div className="plan-dropdown-option-specs">
                      {formatSpecs(size)}
                    </div>
                    {getDisabledMessage(size) && (
                      <div className="plan-dropdown-option-requirement">
                        {getDisabledMessage(size)}
                      </div>
                    )}
                  </div>
                  {value === size.slug && (
                    <svg
                      className="check-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M13.3337 4L6.00033 11.3333L2.66699 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              ))
            ) : (
              <div className="plan-dropdown-empty">No plans found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanDropdown;
